import type { ExplorationView, JudgedCandidate, StructuredQuery } from "@shared/labs/sourcing";
import { Download, Layers, Loader2, RotateCcw, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { BriefStep } from "./brief-step";
import { CandidateCard } from "./candidate-card";
import { CreateSetDialog } from "./create-set-dialog";
import { FilterEditor } from "./filter-editor";
import { downloadJson } from "./lib";
import { EMPTY_LA_SCOPE, type LaScopeSelection } from "./model-picker";
import { type FlowProject, RunProjectActions } from "./projects/run-project-actions";
import { RecentExplorations } from "./recent-explorations";
import { ShareLinkButton } from "./share-link-button";

const POLL_INTERVAL_MS = 2000;
/** Level-Assessment evaluation sets are capped at 20 candidates. */
const MAX_SET = 20;

function defaultSetName(brief: string): string {
  const snippet = brief.replace(/\s+/g, " ").trim().slice(0, 60);
  return snippet ? `Sourcing — ${snippet}` : "Sourcing";
}

/**
 * "Busca precisa" — the direct-control flow. The recruiter describes the role, the
 * model proposes filters, the recruiter edits EVERY filter, then runs ONE exact
 * faceted search (no bandit). The result is the same FIT-ranked, explained
 * shortlist as Explore — push to a Level-Assessment set or export to CSV. The run
 * is stored as a precise-kind exploration, so polling/history/results are shared.
 */
export function PreciseFlow({
  initialExplorationId,
  project,
}: {
  initialExplorationId?: string;
  project?: FlowProject;
} = {}) {
  const { toast } = useToast();
  // A project session starts from the project's brief + LA scope (editable —
  // iterating the brief per session is the point; the project keeps its own).
  const [brief, setBrief] = useState(project?.brief ?? "");
  const [laScope, setLaScope] = useState<LaScopeSelection>(
    project
      ? { modelId: project.laModelId, disciplineId: project.laDisciplineId, level: project.laLevel }
      : EMPTY_LA_SCOPE,
  );
  const [query, setQuery] = useState<StructuredQuery | null>(null);
  const [view, setView] = useState<ExplorationView | null>(null);
  const [busy, setBusy] = useState(false);
  // Start in the reopening state when we arrive via a shared deep-link, so the very
  // first render shows the spinner instead of briefly flashing the create flow (which
  // would also fire a labs-only `list` call the shared viewer can't make).
  const [reopening, setReopening] = useState(Boolean(initialExplorationId));

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [setDialogOpen, setSetDialogOpen] = useState(false);
  const selectionTouched = useRef(false);

  const idRef = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const results = view?.results ?? [];
  // Seed the LA-set selection to the top-20 by FIT until the recruiter edits it.
  useEffect(() => {
    if (selectionTouched.current || !view?.results?.length) return;
    setSelectedIds(new Set(view.results.slice(0, MAX_SET).map((c) => c.id)));
  }, [view?.results]);

  const toggleSelect = (id: string) => {
    selectionTouched.current = true;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_SET) next.add(id);
      return next;
    });
  };

  const selectedCandidates = useMemo(
    () => results.filter((c) => selectedIds.has(c.id)),
    [results, selectedIds],
  );
  const atCap = selectedIds.size >= MAX_SET;

  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
      idRef.current = null;
    };
  }, []);

  // Shared deep-link (`?exploration=…`): open that run's results on mount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reopen is stable enough; key off the id so it runs once per shared link
  useEffect(() => {
    if (initialExplorationId) void reopen(initialExplorationId);
  }, [initialExplorationId]);

  function stopPolling() {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }

  function poll(id: string) {
    rpcClient.labs
      .sourcingExplorationGet({ explorationId: id })
      .then((v) => {
        if (idRef.current !== id) return;
        setView(v);
        if (v.status === "done" || v.status === "error") {
          setBusy(false);
          stopPolling();
          if (v.status === "error") {
            toast({
              title: "Falha na busca",
              description: v.errorMessage ?? "A busca terminou com erro.",
              variant: "destructive",
            });
          }
          return;
        }
        setBusy(true);
        pollRef.current = setTimeout(() => poll(id), POLL_INTERVAL_MS);
      })
      .catch((err) => {
        if (idRef.current !== id) return;
        setBusy(false);
        stopPolling();
        toast({
          title: "Falha na busca",
          description: err instanceof Error ? err.message : "Erro inesperado",
          variant: "destructive",
        });
      });
  }

  async function start(outQuery: StructuredQuery, pages: number) {
    selectionTouched.current = false;
    setSelectedIds(new Set());
    setBusy(true);
    setView(null);
    try {
      const { explorationId } = await rpcClient.labs.sourcingPreciseStart({
        query: outQuery,
        pages,
        laModelId: laScope.modelId ?? undefined,
        laDisciplineId: laScope.disciplineId ?? undefined,
        laLevel: laScope.level ?? undefined,
        projectId: project?.id,
      });
      idRef.current = explorationId;
      poll(explorationId);
    } catch (err) {
      setBusy(false);
      toast({
        title: "Falha ao iniciar a busca",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    }
  }

  function reset() {
    stopPolling();
    idRef.current = null;
    setView(null);
    setQuery(null);
    setBrief("");
    setLaScope(EMPTY_LA_SCOPE);
    setBusy(false);
    setReopening(false);
    selectionTouched.current = false;
    setSelectedIds(new Set());
  }

  async function reopen(id: string) {
    stopPolling();
    setQuery(null);
    setView(null);
    setBrief("");
    setReopening(true);
    selectionTouched.current = false;
    setSelectedIds(new Set());
    idRef.current = id;
    try {
      const v = await rpcClient.labs.sourcingExplorationGet({ explorationId: id });
      if (idRef.current !== id) return;
      setView(v);
      if (v.status === "done" || v.status === "error") {
        setBusy(false);
      } else {
        setBusy(true);
        pollRef.current = setTimeout(() => poll(id), POLL_INTERVAL_MS);
      }
    } catch (err) {
      if (idRef.current !== id) return;
      idRef.current = null;
      toast({
        title: "Falha ao reabrir a busca",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      if (idRef.current === id || idRef.current === null) setReopening(false);
    }
  }

  if (reopening) {
    return (
      <section className="space-y-5">
        <SearchingView reopening />
      </section>
    );
  }

  if (view) {
    const done = view.status === "done";
    const running = !done && view.status !== "error";
    return (
      <section className="space-y-5">
        <ProgressHeader view={view} running={running} />

        {view.results.length > 0 && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold">
                  {done ? "Candidatos encontrados" : "Candidatos avaliados até agora"}
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  <span className="tabular-nums">{selectedCandidates.length}</span> / {MAX_SET}{" "}
                  selecionados para avaliação de nível — marque ou desmarque abaixo.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  {view.results.length} avaliados
                </span>
                <Button
                  size="sm"
                  onClick={() => setSetDialogOpen(true)}
                  disabled={selectedCandidates.length === 0}
                >
                  <Layers /> Avaliar no Level Assessment
                  {selectedCandidates.length > 0 && (
                    <span className="ml-1 tabular-nums opacity-80">
                      ({selectedCandidates.length})
                    </span>
                  )}
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadJson(view)}>
                  <Download /> Baixar JSON
                </Button>
                <ShareLinkButton explorationId={view.id} kind="precise" />
              </div>
            </div>

            <CreateSetDialog
              open={setDialogOpen}
              onOpenChange={setSetDialogOpen}
              candidates={selectedCandidates}
              prefillScope={laScope}
              defaultName={defaultSetName(brief || view.brief)}
            />

            {view.results.map((c, i) => (
              <ResultCard
                key={c.id}
                candidate={c}
                rank={i + 1}
                selected={selectedIds.has(c.id)}
                selectionDisabled={atCap}
                onToggleSelect={() => toggleSelect(c.id)}
              />
            ))}
          </div>
        )}

        {running && view.results.length === 0 && <SearchingView />}
        {done && view.results.length === 0 && <EmptyView />}

        {done && (
          <div className="flex items-center justify-between border-t border-border pt-4">
            <span className="text-xs text-muted-foreground">
              {view.results.length} candidatos · {view.pagesUsed} páginas
            </span>
            <div className="flex items-center gap-2">
              <RunProjectActions view={view} />
              <Button variant="outline" onClick={reset}>
                <RotateCcw />
                Nova busca
              </Button>
            </div>
          </div>
        )}
      </section>
    );
  }

  if (!query) {
    return (
      <div className="space-y-2">
        <BriefStep
          brief={brief}
          setBrief={setBrief}
          onParsed={setQuery}
          laScope={laScope}
          setLaScope={setLaScope}
        />
        <RecentExplorations kind="precise" onReopen={reopen} />
      </div>
    );
  }

  return <FilterEditor query={query} busy={busy} onBack={() => setQuery(null)} onStart={start} />;
}

function ProgressHeader({ view, running }: { view: ExplorationView; running: boolean }) {
  const label = running
    ? "Buscando e avaliando candidatos…"
    : `Concluído · ${view.results.length} candidatos`;
  const pct = Math.min(100, Math.round((view.pagesUsed / Math.max(1, view.budgetPages)) * 100));
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-sm font-medium">
          {running && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          {label}
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground tabular-nums">
          {view.results.length} avaliados · {view.pagesUsed}/{view.budgetPages} págs
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-emerald-500/70" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ResultCard({
  candidate,
  rank,
  selected,
  selectionDisabled,
  onToggleSelect,
}: {
  candidate: JudgedCandidate;
  rank: number;
  selected: boolean;
  selectionDisabled: boolean;
  onToggleSelect: () => void;
}) {
  if (!candidate.judgement) return null;
  return (
    <CandidateCard
      candidate={candidate}
      fit={candidate.judgement}
      rank={rank}
      selectable
      selected={selected}
      selectionDisabled={selectionDisabled}
      onToggleSelect={onToggleSelect}
    />
  );
}

function SearchingView({ reopening = false }: { reopening?: boolean }) {
  return (
    <section className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card px-6 py-12 text-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      <p className="text-sm font-medium">
        {reopening ? "Carregando busca salva…" : "Buscando e avaliando candidatos…"}
      </p>
      <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
        {reopening
          ? "Recuperando a lista já avaliada — sem custo adicional de busca."
          : "Rodando a busca exata com os seus filtros e avaliando cada perfil por FIT. Os candidatos aparecem aqui em instantes."}
      </p>
    </section>
  );
}

function EmptyView() {
  return (
    <section className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card px-6 py-12 text-center">
      <Search className="h-6 w-6 text-muted-foreground" />
      <p className="text-sm font-medium">Nenhum candidato encontrado</p>
      <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
        Os filtros podem estar muito restritivos. Volte e relaxe alguns critérios — remova um setor,
        amplie a faixa de senioridade ou tire a experiência mínima.
      </p>
    </section>
  );
}
